import { useState } from 'react'
import { AlertTriangle, Check, CircleSlash, ListChecks, Loader2, Pencil, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { AgentProposal, AgentProposalChange } from '@/lib/api/agent'
import { cn } from '@/lib/utils'

interface Props {
  /** Proposals made together; one card with a box for each when there are several. */
  proposals: AgentProposal[]
  onApprove: (ids: string[], edits: Record<string, Record<string, unknown>>) => Promise<void>
  onDismiss: (ids: string[]) => Promise<void>
}

/** A browser datetime-local value for an ISO instant, in the reader's own time. */
function toLocalInput(iso: unknown) {
  if (typeof iso !== 'string' || !iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function FieldEditor({
  change,
  value,
  onChange,
  disabled,
}: {
  change: AgentProposalChange
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}) {
  const kind = change.edit?.kind
  const common = { 'aria-label': change.label, disabled }
  if (kind === 'select') {
    return (
      <select
        {...common}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm dark:bg-input/30"
      >
        {(change.edit?.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }
  if (kind === 'boolean') {
    return (
      <Checkbox
        {...common}
        checked={Boolean(value)}
        onCheckedChange={(checked) => onChange(checked === true)}
      />
    )
  }
  if (kind === 'textarea') {
    return (
      <Textarea
        {...common}
        rows={3}
        value={String(value ?? '')}
        onChange={(event) => onChange(event.target.value)}
      />
    )
  }
  if (kind === 'datetime') {
    return (
      <Input
        {...common}
        type="datetime-local"
        className="h-8"
        value={toLocalInput(value)}
        onChange={(event) =>
          onChange(event.target.value ? new Date(event.target.value).toISOString() : null)
        }
      />
    )
  }
  return (
    <Input
      {...common}
      type={kind === 'number' ? 'number' : 'text'}
      className="h-8"
      value={String(value ?? '')}
      onChange={(event) =>
        onChange(
          kind === 'number'
            ? event.target.value === ''
              ? null
              : Number(event.target.value)
            : event.target.value,
        )
      }
    />
  )
}

/** What happened to a proposal, once decided, in a line. */
function Outcome({ proposal }: { proposal: AgentProposal }) {
  if (proposal.status === 'EXECUTING') {
    return (
      <p className="flex items-center gap-1.5 text-muted-foreground" role="status">
        <Loader2 aria-hidden className="size-3.5 animate-spin" />
        Working on it…
      </p>
    )
  }
  if (proposal.status === 'EXECUTED') {
    return (
      <p className="flex items-center gap-1.5 text-done" role="status">
        <Check aria-hidden className="size-3.5" />
        Done.
      </p>
    )
  }
  if (proposal.status === 'DISMISSED') {
    return (
      <p className="text-muted-foreground" role="status">
        Dismissed. Nothing was changed.
      </p>
    )
  }
  if (proposal.status === 'EXPIRED') {
    return (
      <p className="text-muted-foreground" role="status">
        Expired, so nothing was changed. Ask Beacon again if it still needs doing.
      </p>
    )
  }
  if (proposal.status === 'CONFLICT') {
    return (
      <div className="space-y-1 text-waiting" role="status">
        <p className="flex items-center gap-1.5 font-medium">
          <AlertTriangle aria-hidden className="size-3.5" />
          Not done: it changed since Beacon proposed this.
        </p>
        {proposal.conflict?.length ? (
          <ul className="list-disc pl-5 text-foreground">
            {proposal.conflict.map((entry) => (
              <li key={entry.field}>
                {entry.label} is now {entry.now} (it was {entry.proposed_from} when proposed).
              </li>
            ))}
          </ul>
        ) : (
          proposal.error && <p className="text-foreground">{proposal.error}</p>
        )}
      </div>
    )
  }
  if (proposal.status === 'FAILED') {
    return (
      <p className="flex items-start gap-1.5 text-destructive" role="status">
        <CircleSlash aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        Not done: {proposal.error ?? 'Helm refused it.'}
      </p>
    )
  }
  return proposal.error ? <p className="text-destructive">{proposal.error}</p> : null
}

/**
 * Actions Beacon proposed, waiting on the person.
 *
 * Nothing has happened when this appears. Approve carries the ticked ones out
 * as the person, through the same route the record's page uses; Edit changes
 * the proposed values first; Dismiss drops them. The card shows what each
 * changes, from what to what, and how it went.
 */
export function ProposalCard({ proposals, onApprove, onDismiss }: Props) {
  const pending = proposals.filter((proposal) => proposal.status === 'PENDING')
  const many = proposals.length > 1
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(pending.map((proposal) => proposal.id)),
  )
  const [editing, setEditing] = useState(false)
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({})
  const [busy, setBusy] = useState(false)

  const chosen = pending.filter((proposal) => !many || selected.has(proposal.id))
  const editable = pending.some((proposal) =>
    proposal.changes.some((change) => change.edit && proposal.editable.includes(change.field)),
  )

  const valueOf = (proposal: AgentProposal, change: AgentProposalChange) =>
    edits[proposal.id] && change.field in edits[proposal.id]!
      ? edits[proposal.id]![change.field]
      : change.after

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    try {
      await action()
    } finally {
      setBusy(false)
      setEditing(false)
    }
  }

  return (
    <section
      aria-label={many ? 'Proposed changes' : 'Proposed change'}
      // Found by the call's "to approve" button, and focused when it is pressed.
      data-awaiting-approval={pending.length > 0 ? '' : undefined}
      tabIndex={-1}
      className={cn(
        'min-w-0 scroll-mt-3 rounded-xl border bg-card p-4 text-sm outline-none',
        pending.length > 0 && 'border-waiting/50',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <ListChecks
          aria-hidden
          className={cn('size-4', pending.length ? 'text-waiting' : 'text-muted-foreground')}
        />
        <span className="font-medium">
          {many ? `Beacon proposes ${proposals.length} changes` : 'Beacon proposes'}
        </span>
        {pending.length > 0 && (
          <Badge variant="outline" className="text-xs">
            Needs your approval
          </Badge>
        )}
      </div>

      <ol className="mt-3 space-y-3">
        {proposals.map((proposal) => {
          const open = proposal.status === 'PENDING'
          return (
            <li
              key={proposal.id}
              className={cn('space-y-2', many && 'rounded-lg border border-border/70 p-3')}
            >
              <div className="flex items-start gap-2.5">
                {many && open && (
                  <Checkbox
                    className="mt-0.5"
                    aria-label={proposal.summary}
                    checked={selected.has(proposal.id)}
                    disabled={busy}
                    onCheckedChange={(checked) =>
                      setSelected((current) => {
                        const next = new Set(current)
                        if (checked === true) next.add(proposal.id)
                        else next.delete(proposal.id)
                        return next
                      })
                    }
                  />
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-foreground">{proposal.summary}</p>
                  <p className="text-xs text-muted-foreground">
                    {proposal.title} ·{' '}
                    {proposal.target_path ? (
                      <a className="underline-offset-4 hover:underline" href={proposal.target_path}>
                        {proposal.target_label}
                      </a>
                    ) : (
                      proposal.target_label
                    )}
                  </p>
                </div>
              </div>

              <dl className="grid grid-cols-[minmax(0,8rem)_1fr] gap-x-3 gap-y-1.5">
                {proposal.changes.map((change) => (
                  <div key={change.field} className="contents">
                    <dt className="truncate text-muted-foreground">{change.label}</dt>
                    <dd className="min-w-0 break-words">
                      {editing &&
                      open &&
                      change.edit &&
                      proposal.editable.includes(change.field) ? (
                        <FieldEditor
                          change={change}
                          value={valueOf(proposal, change)}
                          disabled={busy}
                          onChange={(value) =>
                            setEdits((current) => ({
                              ...current,
                              [proposal.id]: { ...current[proposal.id], [change.field]: value },
                            }))
                          }
                        />
                      ) : change.before_text ? (
                        <>
                          <span className="text-muted-foreground line-through decoration-1">
                            {change.before_text}
                          </span>{' '}
                          <span aria-hidden>→</span>{' '}
                          <span className="font-medium">{change.after_text}</span>
                        </>
                      ) : (
                        <span className="whitespace-pre-wrap font-medium">{change.after_text}</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              {proposal.effects.length > 0 && open && (
                <div className="rounded-md bg-muted/50 px-3 py-2">
                  <p className="text-xs font-medium text-muted-foreground">What this does</p>
                  <ul className="mt-1 space-y-0.5">
                    {proposal.effects.map((effect) => (
                      <li key={effect}>{effect}</li>
                    ))}
                  </ul>
                </div>
              )}
              {proposal.reason && !proposal.changes.some((change) => change.field === 'reason') && (
                <p className="text-xs text-muted-foreground">Reason: {proposal.reason}</p>
              )}

              <Outcome proposal={proposal} />
            </li>
          )
        })}
      </ol>

      {pending.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={busy || chosen.length === 0}
            onClick={() =>
              void run(() =>
                onApprove(
                  chosen.map((proposal) => proposal.id),
                  edits,
                ),
              )
            }
          >
            {busy ? <Loader2 className="animate-spin" /> : <Check />}
            {many ? `Approve ${chosen.length} of ${pending.length}` : 'Approve'}
          </Button>
          {editable && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              aria-pressed={editing}
              onClick={() => setEditing((value) => !value)}
            >
              <Pencil />
              {editing ? 'Done editing' : 'Edit'}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || chosen.length === 0}
            onClick={() => void run(() => onDismiss(chosen.map((proposal) => proposal.id)))}
          >
            <X />
            {many ? 'Dismiss selected' : 'Dismiss'}
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            Nothing changes until you approve. It is done as you, with your permissions.
          </p>
        </div>
      )}
    </section>
  )
}
